import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Receipt, MapPin, CreditCard, Calendar, Package, AlertCircle, CheckCircle2, Copy, StickyNote } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { fmtIDR, productImg, LOGO } from "@/lib/ellaApi";
import { toast } from "sonner";

const STATUS_LABEL = {
  1: { label: "Menunggu Pembayaran", color: "bg-amber-50 text-amber-700" },
  2: { label: "Diproses", color: "bg-blue-50 text-blue-700" },
  3: { label: "Dikirim", color: "bg-indigo-50 text-indigo-700" },
  4: { label: "Selesai", color: "bg-emerald-50 text-emerald-700" },
  5: { label: "Dibatalkan", color: "bg-red-50 text-red-700" },
};

const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Disalin"); };

export default function TransactionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ella.getTransactionDetail(id)
      .then((res) => setData(res.status ? res.data : null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="p-4 space-y-2 pb-24">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 skeleton rounded-2xl" />)}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pb-24">
        <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
          <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button"><ChevronLeft className="w-5 h-5" /></button>
          <h1 className="font-display text-lg font-bold text-gray-900">Detail Pesanan</h1>
        </div>
        <div className="py-12 text-center"><Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">Detail transaksi tidak ditemukan</p></div>
      </div>
    );
  }

  const pay = data.paymentData || {};
  const orders = data.orderData || [];
  const status = orders[0]?.Status ?? (pay.IsPaid === 1 ? 2 : 1);
  const badge = STATUS_LABEL[status] || { label: "Status Tidak Diketahui", color: "bg-gray-100 text-gray-600" };

  const subtotal = orders.reduce((s, o) => s + Number(o.SubTotal || 0), 0);
  const deliveryFee = orders.reduce((s, o) => s + Number(o.DeliveryFee || 0), 0);
  const discount = orders.reduce((s, o) => s + Number(o.SubDiscount || 0) + Number(o.Discount || 0), 0);
  const total = Number(pay.GrossAmount || subtotal + deliveryFee - discount);
  const [methodCode, methodLabel] = (pay.PaymentMethod || "|").split("|");
  const cancelReason = orders.find((o) => o.CancelledReason)?.CancelledReason;

  return (
    <div className="pb-24">
      <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display text-lg font-bold text-gray-900">Detail Pesanan</h1>
      </div>

      <div className="p-4 space-y-3">
        {/* Status header */}
        <section className="bg-white rounded-2xl border border-gray-100 p-3" data-testid="trx-status-section">
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${badge.color}`} data-testid="trx-status-badge">{badge.label}</span>
            {pay.IsPaid === 1 ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Lunas</span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-amber-600 font-semibold"><AlertCircle className="w-3.5 h-3.5" /> Belum Lunas</span>
            )}
          </div>
          {cancelReason && (
            <p className="mt-2 text-[11px] text-red-600 bg-red-50 rounded-lg px-2 py-1.5 leading-relaxed">{cancelReason}</p>
          )}
        </section>

        {/* Invoice info */}
        <section className="bg-white rounded-2xl border border-gray-100 p-3 space-y-1.5 text-sm" data-testid="trx-invoice-section">
          <div className="flex justify-between items-center gap-2">
            <span className="text-gray-500 text-xs flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> No. Invoice</span>
            <button onClick={() => copy(pay.ID || id)} className="font-mono font-semibold text-xs text-gray-900 flex items-center gap-1" data-testid="trx-invoice-copy">
              {pay.ID || id} <Copy className="w-3 h-3 text-gray-400" />
            </button>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Tanggal Pesan</span>
            <span className="font-medium">{pay.CreatedDate || "-"}</span>
          </div>
          {pay.PaidDate && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Tanggal Bayar</span>
              <span className="font-medium">{pay.PaidDate}</span>
            </div>
          )}
          {/* Invoice becomes downloadable once the order is no longer unpaid (legacy rule) */}
          {status !== 1 && data.ID && (
            <a
              href={ella.invoiceUrl(data.ID)}
              target="_blank"
              rel="noreferrer"
              className="block text-center text-xs font-semibold text-[#D32F2F] border border-red-200 rounded-full py-2 mt-2 hover:bg-red-50"
              data-testid="trx-invoice-download"
            >
              Unduh Invoice
            </a>
          )}
        </section>

        {/* Shipping address */}
        <section data-testid="trx-shipping-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#D32F2F]" /> Alamat Pengiriman
          </h2>
          <div className="bg-white rounded-2xl border border-gray-100 p-3">
            <p className="font-semibold text-sm text-gray-900" data-testid="trx-shipping-name">{pay.Name || "-"} • {pay.Phone || "-"}</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed" data-testid="trx-shipping-address">
              {[pay.Address, pay.DistrictName, pay.CityName, pay.StateName, pay.PostalCode].filter(Boolean).join(", ") || "-"}
            </p>
          </div>
        </section>

        {/* Items */}
        <section data-testid="trx-items-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
            <Package className="w-4 h-4 text-[#D32F2F]" /> Produk Pesanan ({orders.length})
          </h2>
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
            {orders.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Belum ada produk</p>
            ) : (
              orders.map((o) => {
                const itemSubtotal = Number(o.ItemPrice || o.SourcePrice || 0) * Number(o.Qty || 0);
                return (
                  <div key={o.ID} className="p-3 flex gap-3" data-testid={`trx-item-${o.ID}`}>
                    <img
                      src={productImg(o.ImagePath)}
                      alt={o.Product}
                      className="w-14 h-14 rounded-xl object-cover bg-gray-50 shrink-0"
                      onError={(e) => { e.target.src = LOGO; e.target.classList.add("object-contain", "p-2", "opacity-40"); }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug" data-testid={`trx-item-name-${o.ID}`}>{o.Product}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">Cabang {o.Branch}</p>
                      {o.Notes && (
                        <p className="text-[11px] text-gray-500 mt-0.5 flex items-start gap-1">
                          <StickyNote className="w-3 h-3 shrink-0 mt-0.5" />
                          <span className="italic">{o.Notes}</span>
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-600">{fmtIDR(o.ItemPrice || o.SourcePrice || 0)} × {o.Qty}</span>
                        <span className="text-sm font-bold text-gray-900">{fmtIDR(itemSubtotal)}</span>
                      </div>
                      {o.TrackingNumber && (
                        <p className="text-[11px] text-indigo-600 mt-1">📦 Resi: <b>{o.TrackingNumber}</b></p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Payment method */}
        {methodLabel && (
          <section data-testid="trx-payment-section">
            <h2 className="font-display font-bold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
              <CreditCard className="w-4 h-4 text-[#D32F2F]" /> Metode Pembayaran
            </h2>
            <div className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-2">
              <span className="text-[10px] font-bold text-gray-500 uppercase">{methodCode}</span>
              <span className="text-sm font-semibold text-gray-900" data-testid="trx-payment-method">{methodLabel}</span>
            </div>
          </section>
        )}

        {/* Cost breakdown */}
        <section className="bg-white rounded-2xl border border-gray-100 p-3 space-y-1.5 text-sm" data-testid="trx-summary-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-1">Rincian Pembayaran</h2>
          <div className="flex justify-between"><span className="text-gray-500">Subtotal Produk</span><span data-testid="trx-subtotal">{fmtIDR(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Ongkos Kirim</span><span data-testid="trx-shipping-fee">{fmtIDR(deliveryFee)}</span></div>
          {discount > 0 && (
            <div className="flex justify-between text-emerald-600"><span>Diskon</span><span data-testid="trx-discount">-{fmtIDR(discount)}</span></div>
          )}
          <div className="border-t border-gray-100 pt-2 flex justify-between">
            <span className="font-display font-bold">Total Pembayaran</span>
            <span className="font-display font-bold text-lg text-[#D32F2F]" data-testid="trx-grand-total">{fmtIDR(total)}</span>
          </div>
        </section>

        {/* Legacy "Hubungi Penjual" — chat with the branch admin, prefilled with the invoice */}
        {orders[0]?.BranchID && (
          <button
            onClick={() => nav(`/messages/${orders[0].BranchID}`, {
              state: {
                branchName: orders[0].Branch,
                prefill: `Halo Admin ${orders[0].Branch}, saya mau menanyakan pesanan dengan No. Invoice ${pay.ID || id}.`,
              },
            })}
            className="w-full h-11 rounded-full border border-red-200 text-[#D32F2F] font-semibold text-sm hover:bg-red-50"
            data-testid="trx-contact-seller"
          >
            Hubungi Penjual
          </button>
        )}
      </div>
    </div>
  );
}
